/*
  <shintop-kit-builder> — the kit builder's progressive enhancement (sections/shintop-kit-builder.liquid).

  Without this file the section is a working form: real radios, the default size, the server's
  totals, and a plain POST of items[n][id] / items[n][quantity] to /cart/add. With it:
    - a swap or a size change updates the packs, every card's price difference and the total;
    - the add is one JSON POST to /cart/add.js with the sections window.shintopCart asks for, then
      shintopCart.afterCartWrite — the drawer, the bubble, the open and the one publish.
  Prices are read from each radio's data-price (cents, the store's own variant price). Strings come
  from data-str-* attributes the section fills from the locale, never from this file.
*/
class ShintopKitBuilder extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: this.dataset.currency || 'AUD' });
    this.form.addEventListener('change', (event) => this.onChange(event));
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.update();
  }

  get size() {
    const picked = this.form.querySelector('input[name="kit_size"]:checked');
    return picked ? Number(picked.value) : 0;
  }

  str(name, value) {
    const template = this.dataset['str' + name] || '';
    return template.replace('__X__', value);
  }

  format(cents) {
    return this.money.format(cents / 100);
  }

  onChange(event) {
    if (event.target.matches('input[type="radio"]')) this.update();
  }

  /*
    Stock is per SIZE (2026-10-20, Naoya's Serves 4 add: a stand-in with 1 left was asked for 2 and
    Shopify refused it). A tracked choice with fewer packs left than the size needs behaves like a
    sold-out one for that size — disabled, "Only N left" — and if it was the pick, the pick moves to
    the first choice that has enough, as the server's default already does. No choice with enough →
    the part says so and the button is blocked (aria-disabled + the submit guard).
  */
  update() {
    const large = this.size === 1;
    let total = 0;
    let blocked = false;
    // What the kit makes at each size: the smallest packs x yield over the counted parts (the part
    // that runs out first). A part with no data-yield anywhere outlasts the box and is not counted;
    // a counted part whose pick has no yield turns the line off — never a guessed number.
    const makes = [Infinity, Infinity];
    const totals = [0, 0];
    let counted = 0;
    let yieldOk = true;
    const yieldLines = [];

    this.querySelectorAll('[data-part]').forEach((part) => {
      const qty = Number(large ? part.dataset.qtyLarge : part.dataset.qtySmall) || 1;
      const qtyInput = part.querySelector('[data-qty-input]');
      if (qtyInput) qtyInput.value = qty;

      const packs = part.querySelector('[data-packs]');
      if (packs) {
        packs.textContent = qty === 1 ? this.str('Packone') : this.str('Packs', qty);
        packs.hidden = qty < 2;
      }

      const inputs = [...part.querySelectorAll('.shintop-kitb__input')];
      const short = (input) => input.dataset.stock !== undefined && Number(input.dataset.stock) < qty;
      inputs.forEach((input) => {
        input.disabled = input.hasAttribute('data-soldout') || short(input);
      });

      let checked = inputs.find((input) => input.checked && !input.disabled);
      if (!checked) {
        inputs.forEach((input) => (input.checked = false));
        checked = inputs.find((input) => !input.disabled);
        if (checked) checked.checked = true;
      }
      const out = part.querySelector('[data-part-out]');
      if (out) out.hidden = Boolean(checked);
      if (!checked) blocked = true;

      const base = checked ? Number(checked.dataset.price) : 0;
      if (checked) total += base * qty;

      if (inputs.some((input) => input.dataset.yield !== undefined)) {
        counted += 1;
        const each = checked ? Number(checked.dataset.yield) || 0 : 0;
        if (!each) yieldOk = false;
        const small = Number(part.dataset.qtySmall) || 1;
        const bigger = Number(part.dataset.qtyLarge) || small;
        makes[0] = Math.min(makes[0], Math.floor(each * small));
        makes[1] = Math.min(makes[1], Math.floor(each * bigger));
      }
      if (checked) {
        totals[0] += Number(checked.dataset.price) * (Number(part.dataset.qtySmall) || 1);
        totals[1] += Number(checked.dataset.price) * (Number(part.dataset.qtyLarge) || Number(part.dataset.qtySmall) || 1);
      }

      inputs.forEach((input) => {
        const card = input.closest('.shintop-kitb__card');
        const diffEl = card.querySelector('[data-diff]');
        const yieldEl = card.querySelector('[data-yield-line]');
        if (yieldEl) yieldLines.push([yieldEl, Math.floor(Number(input.dataset.yield) * qty)]);
        card.classList.toggle('is-selected', input.checked);
        card.classList.toggle('is-soldout', input.disabled);
        if (!diffEl) return;
        if (input.hasAttribute('data-soldout')) {
          diffEl.textContent = this.str('Soldout');
        } else if (short(input)) {
          diffEl.textContent = this.str('Fewleft', input.dataset.stock);
        } else if (input.checked) {
          diffEl.textContent = this.str('Inkit');
        } else {
          const diff = (Number(input.dataset.price) - base) * qty;
          if (diff > 0) diffEl.textContent = this.str('More', this.format(diff));
          else if (diff < 0) diffEl.textContent = this.str('Less', this.format(-diff));
          else diffEl.textContent = this.str('Same');
        }
      });
    });

    const totalEl = this.querySelector('[data-total]');
    if (totalEl) totalEl.textContent = this.format(total);
    const hasYield = counted > 0 && yieldOk;
    const kitMakes = makes[large ? 1 : 0];
    yieldLines.forEach(([el, n]) => {
      const spare = hasYield ? n - kitMakes : 0;
      const makesEl = el.querySelector('[data-yield-makes]');
      const spareEl = el.querySelector('[data-yield-spare]');
      if (makesEl) makesEl.textContent = this.str('Makescard', n);
      if (spareEl) spareEl.textContent = spare > 0 ? this.str('Spare', spare) : '';
    });
    this.form.querySelectorAll('[data-size-total]').forEach((el) => {
      const input = el.closest('label').querySelector('input[name="kit_size"]');
      const i = Number(input.value);
      el.textContent = this.format(totals[i]);
      const serve = el.parentElement.querySelector('[data-size-serve]');
      if (serve && hasYield && makes[i] > 0) serve.textContent = this.str('Perserve', this.format(Math.floor(totals[i] / makes[i])));
    });
    this.form.querySelectorAll('input[name="kit_size"]').forEach((input) => {
      const name = input.closest('label').querySelector('.shintop-kitb__size-name');
      if (!name) return;
      name.textContent = hasYield ? this.str('Makessize', makes[Number(input.value)]) : name.dataset.sizeLabel || name.textContent;
    });
    const sizeName = this.querySelector('[data-size-name]');
    const sizeInput = this.form.querySelector('input[name="kit_size"]:checked');
    if (sizeName && hasYield) {
      sizeName.textContent = this.str('Makestotal', makes[large ? 1 : 0]).trim();
    } else if (sizeName && sizeInput) {
      sizeName.textContent = sizeInput.closest('label').querySelector('.shintop-kitb__size-name').textContent;
    }
    const button = this.querySelector('.shintop-kitb__add');
    const label = this.querySelector('[data-add-label]');
    if (button && !button.classList.contains('is-adding')) {
      if (blocked) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
      if (label) label.textContent = this.str(blocked ? 'Kitout' : 'Addtocart');
    }
    this.dataset.size = String(this.size);
  }

  /*
    The add, then the shared after-write step (assets/shintop-cart.js) — the one place a Shintop
    control renders the drawer. 2026-10-20: the first version called Dawn's renderContents itself,
    which re-renders `#CartDrawer` but leaves `is-empty` on <cart-drawer>, so a first add into an
    empty cart opened a drawer showing the new total over no lines (Naoya's screenshot). The
    probe had passed because its mocked cart was empty too.
  */
  async onSubmit(event) {
    const button = this.querySelector('.shintop-kitb__add');
    if (button.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }
    if (!window.shintopCart) return; // no helper: let the plain form post to /cart/add
    event.preventDefault();

    // One id per add, so two builds of the same kit stay two kit rows in the cart
    // (snippets/shintop-cart-kit.liquid groups by `_kit`; the section's value is the no-JS one).
    const properties = this.dataset.kitTitle
      ? {
          Kit: this.dataset.kitTitle,
          _kit: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
          _kit_handle: this.dataset.kitHandle || '',
        }
      : null;
    const items = [...this.querySelectorAll('[data-part]')]
      .map((part) => {
        const picked = part.querySelector('.shintop-kitb__input:checked');
        const qty = Number(part.querySelector('[data-qty-input]')?.value) || 1;
        if (!picked) return null;
        return properties ? { id: Number(picked.value), quantity: qty, properties } : { id: Number(picked.value), quantity: qty };
      })
      .filter(Boolean);
    if (!items.length) return;

    const label = this.querySelector('[data-add-label]');
    const error = this.querySelector('[data-error]');
    error.hidden = true;
    button.classList.add('is-adding');
    button.setAttribute('aria-disabled', 'true');
    label.textContent = this.str('Adding');

    try {
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: window.shintopCart.sections(), sections_url: window.location.pathname }),
      });
      const parsed = await response.json();
      if (!response.ok || parsed.status) {
        error.textContent = parsed.description || parsed.message || this.str('Error');
        error.hidden = false;
        // A refused add can still have changed the cart; re-read it so the drawer and the bubble
        // say what Shopify did — never left as the page loaded them (his drawer read "empty").
        window.shintopCart.afterCartWrite({}, { source: 'shintop-kit-builder' });
        return;
      }
      window.shintopCart.afterCartWrite(parsed, { source: 'shintop-kit-builder', open: true, opener: button });
    } catch (e) {
      error.textContent = this.str('Error');
      error.hidden = false;
    } finally {
      button.classList.remove('is-adding');
      this.update();
    }
  }
}

if (!customElements.get('shintop-kit-builder')) customElements.define('shintop-kit-builder', ShintopKitBuilder);
